<template>
  <div class="system-settings-page"><div class="ssp-inner">
    <div class="d-flex align-center mb-6">
      <v-icon large color="white" class="mr-3">mdi-cog-outline</v-icon>
      <div>
        <h1 class="text-h5 font-weight-bold white--text mb-1">{{ $t('systemSettings.title') }}</h1>
        <div class="caption grey--text">{{ $t('systemSettings.subtitle') }}</div>
      </div>
    </div>

    <v-card outlined class="mb-4" style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-card-title class="white--text">
        <v-icon left color="white">mdi-image-outline</v-icon>
        {{ $t('systemSettings.systemLogo') }}
      </v-card-title>
      <v-card-subtitle class="grey--text">
        {{ $t('systemSettings.logoHint') }}
        <span v-if="!isSuperAdmin" class="ml-2" style="color: #FFCA28;">
          <v-icon x-small color="amber">mdi-lock-outline</v-icon>
          {{ $t('systemSettings.superadminOnly') }}
        </span>
      </v-card-subtitle>
      <v-divider></v-divider>

      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="4">
            <div class="logo-preview-wrap">
              <div v-if="previewSrc" class="logo-preview">
                <img :src="previewSrc" alt="Logo systemu">
              </div>
              <div v-else class="logo-placeholder">
                <v-icon size="42" color="grey">mdi-image-off-outline</v-icon>
                <div class="caption grey--text mt-2">{{ $t('systemSettings.noLogo') }}</div>
              </div>
            </div>
            <div v-if="pendingDataUrl" class="caption mt-2 text-center" style="color:#FFCA28;">
              <v-icon x-small color="amber">mdi-alert-circle-outline</v-icon>
              {{ $t('systemSettings.unsavedPreview') }}
            </div>
            <div v-else-if="logoUrl" class="caption grey--text mt-2 text-center">
              {{ $t('systemSettings.currentLogo') }}
            </div>
          </v-col>

          <v-col cols="12" md="8" v-if="isSuperAdmin">
            <div
              class="upload-drop"
              :class="{ dragover: dragOver }"
              @click="$refs.fileInput.click()"
              @dragenter.prevent="dragOver = true"
              @dragover.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="onDrop"
            >
              <v-icon large color="primary">mdi-cloud-upload-outline</v-icon>
              <div class="upload-title">{{ $t('systemSettings.uploadLogo') }}</div>
              <div class="upload-sub">{{ $t('systemSettings.uploadHint') }}</div>
              <input type="file" ref="fileInput" accept="image/*" style="display:none;" @change="onPick">
            </div>

            <v-alert v-if="errorMsg" type="error" dense outlined class="mt-3 mb-0">{{ errorMsg }}</v-alert>

            <div v-if="pendingDataUrl" class="d-flex align-center mt-4" style="gap: 10px;">
              <v-btn color="primary" depressed :loading="saving" @click="save">
                <v-icon small left>mdi-check</v-icon>{{ $t('common.save') }}
              </v-btn>
              <v-btn text @click="resetPending">{{ $t('common.cancel') }}</v-btn>
              <span class="caption grey--text">Wybrano: {{ pendingName }} ({{ pendingSize }})</span>
            </div>

            <div v-else-if="logoUrl" class="mt-4">
              <v-btn small outlined color="error" @click="removeLogo" :loading="removing">
                <v-icon small left>mdi-delete-outline</v-icon>{{ $t('systemSettings.removeLogo') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Regulamin aplikacji kierowcy -->
    <v-card outlined class="mb-4" style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-card-title class="white--text">
        <v-icon left color="white">mdi-file-document-outline</v-icon>
        {{ $t('systemSettings.driverTerms') }}
      </v-card-title>
      <v-card-subtitle class="grey--text">
        {{ $t('systemSettings.driverTermsHint') }}
        <span v-if="!isSuperAdmin" class="ml-2" style="color: #FFCA28;">
          <v-icon x-small color="amber">mdi-lock-outline</v-icon>
          {{ $t('systemSettings.superadminOnly') }}
        </span>
      </v-card-subtitle>
      <v-divider></v-divider>

      <v-card-text>
        <div v-if="terms.exists" class="mb-3 d-flex align-center flex-wrap" style="gap:6px;">
          <v-icon small color="success">mdi-check-circle</v-icon>
          <span class="white--text">{{ terms.originalName }}</span>
          <span class="grey--text caption">wersja {{ terms.version }} · {{ formatTermsDate(terms.updatedAt) }}</span>
          <v-btn small text color="primary" @click="previewTerms">
            <v-icon small left>mdi-eye</v-icon>{{ $t('common.preview') }}
          </v-btn>
          <v-btn small text color="primary" @click="openAcceptancesDialog">
            <v-icon small left>mdi-history</v-icon>{{ $t('systemSettings.driverTermsAcceptances') }}
          </v-btn>
        </div>
        <div v-else class="mb-3 grey--text">{{ $t('systemSettings.driverTermsNone') }}</div>

        <template v-if="isSuperAdmin">
          <v-file-input
            v-model="terms.file"
            accept=".pdf,application/pdf"
            :label="$t('systemSettings.driverTermsPick')"
            outlined
            dense
            dark
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
            show-size
            hide-details
            class="mb-3"
            style="max-width:420px;"
          ></v-file-input>
          <v-btn color="primary" depressed :disabled="!terms.file" :loading="terms.saving" @click="uploadTerms">
            <v-icon small left>mdi-cloud-upload-outline</v-icon>{{ $t('systemSettings.driverTermsUpload') }}
          </v-btn>
          <div class="caption mt-3" style="color:#FFCA28;">
            <v-icon x-small color="amber">mdi-alert-circle-outline</v-icon>
            {{ $t('systemSettings.driverTermsReacceptNote') }}
          </div>
        </template>
      </v-card-text>
    </v-card>

    <v-card outlined class="mb-4" style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-card-title class="white--text">
        <v-icon left color="white">mdi-wallet-plus-outline</v-icon>
        Raport kasowy — saldo początkowe
      </v-card-title>
      <v-card-subtitle class="grey--text">
        Ustaw saldo początkowe dla wybranego miesiąca. Saldo kolejnych miesięcy jest przenoszone automatycznie z salda końcowego poprzedniego miesiąca.
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-select
              v-model.number="balance.month"
              :items="monthOptions"
              :label="$t('common.month')"
              outlined
              dense
              dark
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model.number="balance.year"
              :items="yearOptions"
              :label="$t('common.year')"
              outlined
              dense
              dark
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="balance.amount"
              :label="$t('systemSettings.balancePln')"
              type="number"
              step="0.01"
              outlined
              dense
              dark
            />
          </v-col>
          <v-col cols="12" class="d-flex">
            <v-btn color="primary" @click="saveOpeningBalance" :loading="balance.saving">
              <v-icon left>mdi-content-save</v-icon> {{ $t('systemSettings.saveBalance') }}
            </v-btn>
            <v-spacer />
            <v-btn outlined color="secondary" @click="loadOpeningBalance">
              <v-icon left>mdi-refresh</v-icon> {{ $t('systemSettings.fetchCurrent') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- OCR faktur (AI) — usługa płatna, stąd przełącznik -->
    <v-card outlined class="mb-4" style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-card-title class="white--text">
        <v-icon left color="white">mdi-text-recognition</v-icon>
        OCR faktur AI (kontroler zgodności)
      </v-card-title>
      <v-card-subtitle class="grey--text">
        AI odczytuje ze zdjęcia faktury numer, datę, kwotę, typ dokumentu i NIP, i porównuje z tym, co wpisał kierowca.
        Wskazuje rozbieżności oraz czy jest nasz NIP (faktura uproszczona). Odczyt uruchamia się przy otwieraniu faktury
        do akceptacji. Uzupełnia weryfikację w KSeF. Usługa płatna: ok. 4 gr za dokument.
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="5">
            <v-switch
              v-model="ocr.enabled"
              label="OCR włączony"
              color="success"
              inset
              dark
              :loading="ocr.saving"
              @change="saveOcr"
            />
          </v-col>
          <v-col cols="12" md="7">
            <v-alert v-if="!ocr.configured" dense text type="warning" class="mb-0">
              Brak klucza AI (ANTHROPIC_API_KEY) — OCR nie zadziała.
            </v-alert>
            <div v-else class="caption grey--text">
              Odczyt uruchamia się automatycznie przy otwieraniu faktury do akceptacji (raz na fakturę — wynik jest zapamiętywany).
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined class="mb-4" style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-card-title class="white--text">
        <v-icon left color="white">mdi-cash-register</v-icon>
        {{ $t('systemSettings.cashReportTitle') }}
      </v-card-title>
      <v-card-subtitle class="grey--text">
        {{ $t('systemSettings.cashReportSubtitle') }}
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-switch
              v-model="cashReport.enabled"
              :label="$t('systemSettings.sendingActive')"
              color="success"
              inset
              dark
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="cashReport.email"
              :label="$t('systemSettings.recipientEmail')"
              :placeholder="$t('systemSettings.emailExample')"
              type="email"
              outlined
              dense
              dark
              :disabled="!cashReport.enabled"
            />
          </v-col>
          <v-col cols="12" class="d-flex">
            <v-btn color="primary" @click="saveCashReportConfig" :loading="cashReport.saving">
              <v-icon left>mdi-content-save</v-icon> Zapisz
            </v-btn>
            <v-spacer />
            <v-btn outlined color="warning" @click="testCashReportNow" :loading="cashReport.testing"
              :disabled="!cashReport.enabled || !cashReport.email">
              <v-icon left>mdi-email-fast</v-icon> Wyślij teraz (test)
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined class="mb-4" style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-card-title class="white--text">
        <v-icon left color="white">mdi-file-document-multiple-outline</v-icon>
        Rachunki najmu — miesięczna wysyłka
      </v-card-title>
      <v-card-subtitle class="grey--text">
        5. dnia miesiąca ZIP ze wszystkimi rachunkami PDF za poprzedni miesiąc trafia na wskazany adres (rachunki domykają się 4. dnia — zasada ZUS).
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-switch v-model="rachunki.enabled" :label="$t('systemSettings.sendingActive')" color="success" inset dark />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="rachunki.email" :label="$t('systemSettings.recipientEmail')"
              :placeholder="$t('systemSettings.emailExample')" type="email" outlined dense dark
              :disabled="!rachunki.enabled" />
          </v-col>
          <v-col cols="12" class="d-flex">
            <v-btn color="primary" @click="saveRachunkiConfig" :loading="rachunki.saving">
              <v-icon left>mdi-content-save</v-icon> Zapisz
            </v-btn>
            <v-spacer />
            <v-btn outlined color="warning" @click="testRachunkiNow" :loading="rachunki.testing"
              :disabled="!rachunki.enabled || !rachunki.email">
              <v-icon left>mdi-email-fast</v-icon> Wyślij teraz (test)
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined class="mb-4" style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-card-title class="white--text">
        <v-icon left color="white">mdi-robot-happy-outline</v-icon>
        Asystent biura
      </v-card-title>
      <v-card-subtitle class="grey--text">
        Odpowiada pracownikom na pytania o rozliczenia, kasę i faktury — liczbami z systemu.
        Każde pytanie i odpowiedź trafia do dziennika zdarzeń, więc widać, czy asystent trzyma poziom.
        Wyłączenie ukrywa go w menu i blokuje odpowiedzi.
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="6">
            <v-switch v-model="asystent.enabled" label="Asystent dostępny dla biura"
              color="success" inset dark hide-details :loading="asystent.saving"
              @change="zapiszAsystenta" />
          </v-col>
          <v-col cols="12" md="6">
            <div v-if="!asystent.skonfigurowany" class="caption error--text">
              Brak klucza do usługi AI — asystent nie odpowie, nawet gdy jest włączony.
            </div>
            <div v-else class="caption grey--text">
              Stan: {{ asystent.enabled ? 'włączony' : 'wyłączony' }}.
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined class="mb-4" style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-card-title class="white--text">
        <v-icon left color="white">mdi-email-send-outline</v-icon>
        Faktury kosztowe — wysyłka do księgowości
      </v-card-title>
      <v-card-subtitle class="grey--text">
        Każda faktura leci osobnym mailem z załącznikiem w chwili akceptacji — także dodana ręcznie
        przez biuro i zaakceptowana jako firmowa. Potwierdzenia transakcji nie są wysyłane. Czego nie
        przepuści godzinowy limit poczty, system dobiera sam co godzinę.
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-switch v-model="fakturyMail.enabled" :label="$t('systemSettings.sendingActive')" color="success" inset dark />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="fakturyMail.email" :label="$t('systemSettings.recipientEmail')"
              :placeholder="$t('systemSettings.emailExample')" type="email" outlined dense dark
              :disabled="!fakturyMail.enabled" />
          </v-col>
          <v-col cols="12" class="pt-0">
            <v-switch v-model="fakturyMail.withDetails" color="info" inset dark hide-details
              :disabled="!fakturyMail.enabled"
              label="Dane faktury w treści maila" />
            <div class="caption grey--text mt-1">
              Wyłączone — mail zawiera sam załącznik. Włączone — w treści dochodzi kierowca,
              opis, numer, data wystawienia i kwota brutto.
            </div>
          </v-col>
          <v-col cols="12" class="d-flex">
            <v-btn color="primary" @click="saveFakturyMailConfig" :loading="fakturyMail.saving">
              <v-icon left>mdi-content-save</v-icon> Zapisz
            </v-btn>
            <v-spacer />
            <v-btn outlined color="warning" @click="wyslijZalegleFaktury" :loading="fakturyMail.testing"
              :disabled="!fakturyMail.enabled || !fakturyMail.email">
              <v-icon left>mdi-email-sync-outline</v-icon> Wyślij zaległe teraz
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined class="mb-4" style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-card-title class="white--text">
        <v-icon left color="white">mdi-cellphone-message</v-icon>
        Tryb testowy powiadomień (SMS / e-mail)
      </v-card-title>
      <v-card-subtitle class="grey--text">
        Gdy tryb testowy jest <strong>włączony</strong>, wszystkie SMS-y i e-maile firmy
        są przekierowywane na podany niżej numer i adres testowy — zamiast lecieć na
        prawdziwe adresy kierowców. Wyłącz, aby wysyłać do kierowców.
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-card-text>
        <v-alert
          :type="notifTest.enabled ? 'warning' : 'success'"
          dense
          outlined
          class="mb-4"
        >
          <span v-if="notifTest.enabled">
            <v-icon small left color="warning">mdi-alert</v-icon>
            TRYB TESTOWY WŁĄCZONY — powiadomienia NIE trafiają do kierowców,
            tylko na numer/adres testowy.
          </span>
          <span v-else>
            <v-icon small left color="success">mdi-check-circle</v-icon>
            Tryb produkcyjny — powiadomienia trafiają na prawdziwe adresy kierowców.
          </span>
        </v-alert>
        <v-row>
          <v-col cols="12" md="4">
            <v-switch
              v-model="notifTest.enabled"
              :label="notifTest.enabled ? 'Tryb testowy: WŁĄCZONY' : 'Tryb testowy: wyłączony'"
              color="warning"
              inset
              dark
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="notifTest.phone"
              label="Numer testowy (SMS)"
              placeholder="np. 600 123 456"
              outlined
              dense
              dark
              :disabled="!notifTest.enabled"
              hint="SMS-y zostaną wysłane na ten numer"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="notifTest.email"
              label="E-mail testowy"
              placeholder="np. test@firma.pl"
              type="email"
              outlined
              dense
              dark
              :disabled="!notifTest.enabled"
              hint="E-maile zostaną wysłane na ten adres"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" class="d-flex">
            <v-btn color="primary" @click="saveNotifTestConfig" :loading="notifTest.saving">
              <v-icon left>mdi-content-save</v-icon> Zapisz
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Integracje Bolt/Uber zostały przeniesione do osobnej strony "Integracje API"
         (sidebar → Integracje API). Tu zostaje tylko link żeby admin nie szukał. -->
    <v-card outlined class="mb-4" style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-card-title class="white--text">
        <v-icon left color="white">mdi-api</v-icon>
        Integracje API (Bolt / Uber / FreeNow)
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <div class="grey--text mb-3">
          Konfiguracja API, dane dostępu, status połączeń, ręczny import i historia uruchomień
          cronów przeniesione do dedykowanej strony.
        </div>
        <v-btn color="primary" :to="'/integrations'">
          <v-icon left>mdi-arrow-right</v-icon>
          Przejdź do Integracji API
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Dialog: historia akceptacji regulaminu kierowców -->
    <v-dialog content-class="dark-form-dialog" v-model="acceptDialog.show" max-width="900">
      <v-card>
        <v-card-title>
          {{ $t('systemSettings.driverTermsAcceptances') }}
          <v-spacer></v-spacer>
          <v-chip v-if="acceptDialog.currentVersion" small color="primary" text-color="white">
            {{ $t('systemSettings.driverTermsCurrentVersion') }}: {{ acceptDialog.currentVersion }}
          </v-chip>
        </v-card-title>
        <v-tabs v-model="acceptDialog.tab" background-color="transparent">
          <v-tab>{{ $t('systemSettings.driverTermsAccepted') }} ({{ acceptDialog.total }})</v-tab>
          <v-tab>{{ $t('systemSettings.driverTermsMissing') }} ({{ acceptDialog.missing.length }})</v-tab>
        </v-tabs>
        <v-tabs-items v-model="acceptDialog.tab" touchless>
          <v-tab-item>
            <v-card-text>
              <v-text-field
                v-model="acceptDialog.search"
                :label="$t('common.search') + '...'"
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                hide-details
                class="mb-3"
                style="max-width:320px"
              ></v-text-field>
              <v-data-table
                :headers="acceptHeaders"
                :items="acceptDialog.items"
                :loading="acceptDialog.loading"
                :options.sync="acceptDialog.options"
                :server-items-length="acceptDialog.total"
                :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
                dense
              >
                <template v-slot:[`item.driver`]="{ item }">
                  <span v-if="item.driver">{{ item.driver.firstName }} {{ item.driver.lastName }}</span>
                  <span v-else>—</span>
                </template>
                <template v-slot:[`item.email`]="{ item }">{{ (item.driver && item.driver.email) || '—' }}</template>
                <template v-slot:[`item.version`]="{ item }">
                  <v-chip x-small :color="String(item.version) === String(acceptDialog.currentVersion) ? 'success' : 'grey'" text-color="white">
                    {{ item.version || '—' }}
                  </v-chip>
                </template>
                <template v-slot:[`item.createdAt`]="{ item }">{{ formatDateTime(item.createdAt) }}</template>
                <template v-slot:[`item.ipAddress`]="{ item }">{{ item.ipAddress || '—' }}</template>
              </v-data-table>
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <v-card-text>
              <v-alert v-if="acceptDialog.missing.length === 0" type="success" outlined dense>
                {{ $t('systemSettings.driverTermsNoMissing') }}
              </v-alert>
              <v-simple-table v-else dense>
                <thead>
                  <tr>
                    <th>{{ $t('systemSettings.driverTermsColDriver') }}</th>
                    <th>E-mail</th>
                    <th>{{ $t('systemSettings.driverTermsColPhone') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="d in acceptDialog.missing" :key="d.id">
                    <td>{{ d.firstName }} {{ d.lastName }}</td>
                    <td>{{ d.email || '—' }}</td>
                    <td>{{ d.phone || '—' }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="acceptDialog.show = false">{{ $t('common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Tablety do podpisu. Powstalo 05.10.2026: przeparowanie tabletu zostawia
         DRUGI wpis o tej samej nazwie, a obsluga nie miala go gdzie zobaczyc
         ani usunac — przez tydzien podpisy trafialy na martwy rekord. -->
    <v-card outlined class="mb-4" style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-card-title class="white--text">
        <v-icon left color="white">mdi-tablet-dashboard</v-icon>
        {{ $t('systemSettings.tabletsTitle') }}
      </v-card-title>
      <v-card-subtitle class="grey--text">{{ $t('systemSettings.tabletsHint') }}</v-card-subtitle>
      <v-divider></v-divider>
      <v-card-text>
        <div v-if="podpisy.ladowanie" class="grey--text">
          <v-progress-circular indeterminate size="18" width="2" class="mr-2"></v-progress-circular>
          {{ $t('common.loading') }}
        </div>
        <div v-else-if="!podpisy.urzadzenia.length" class="grey--text">
          {{ $t('systemSettings.tabletsEmpty') }}
        </div>
        <v-simple-table v-else style="background: transparent;">
          <thead>
            <tr>
              <th class="grey--text">{{ $t('systemSettings.tabletName') }}</th>
              <th class="grey--text">{{ $t('systemSettings.tabletPaired') }}</th>
              <th class="grey--text">{{ $t('systemSettings.tabletLastSeen') }}</th>
              <th class="grey--text">{{ $t('systemSettings.tabletState') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in podpisy.urzadzenia" :key="u.id">
              <td class="white--text">{{ u.name }}</td>
              <td class="grey--text">{{ u.pairedAt ? formatDateTime(u.pairedAt) : '—' }}</td>
              <td class="grey--text">
                {{ u.lastSeenAt ? formatDateTime(u.lastSeenAt) : $t('systemSettings.tabletNeverSeen') }}
              </td>
              <td>
                <v-chip x-small :color="milczy(u) ? 'warning' : 'success'" text-color="black">
                  {{ milczy(u) ? $t('systemSettings.tabletSilent') : $t('systemSettings.tabletActive') }}
                </v-chip>
              </td>
              <td class="text-right">
                <v-btn x-small text color="error" :loading="podpisy.usuwanie === u.id"
                       @click="usunUrzadzeniePodpisu(u)">
                  {{ $t('systemSettings.tabletRemove') }}
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" top>
      {{ snackbar.text }}
    </v-snackbar>
  </div></div>
</template>

<script>
const axios = require('axios');
import { isSuperAdmin as isSuperAdminFn } from '@/services/permissions';

export default {
  name: 'SystemSettingsPage',
  data: () => ({
    logoUrl: null,
    pendingFile: null,
    pendingDataUrl: null,
    pendingName: '',
    pendingSize: '',
    dragOver: false,
    saving: false,
    removing: false,
    errorMsg: '',
    snackbar: { show: false, color: 'success', text: '' },
    podpisy: { urzadzenia: [], ladowanie: false, usuwanie: '' },
    userRole: localStorage.getItem('role') || '',
    ocr: { enabled: false, configured: true, saving: false },
    cashReport: { enabled: false, email: '', saving: false, testing: false },
      rachunki: { enabled: false, email: '', saving: false, testing: false },
      fakturyMail: { enabled: false, email: '', withDetails: false, saving: false, testing: false },
      asystent: { enabled: false, skonfigurowany: false, saving: false },
    notifTest: { enabled: false, phone: '', email: '', saving: false },
    terms: { exists: false, originalName: '', version: null, updatedAt: null, file: null, saving: false },
    acceptDialog: {
      show: false,
      tab: 0,
      search: '',
      items: [],
      total: 0,
      options: { page: 1, itemsPerPage: 10 },
      loading: false,
      missing: [],
      currentVersion: null,
    },
    // bolt/uber data przeniesione do IntegrationsPage.vue
    balance: (() => {
      const now = new Date();
      return {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        amount: 0,
        saving: false,
      };
    })(),
    monthOptions: [
      { text: 'Styczeń', value: 1 }, { text: 'Luty', value: 2 }, { text: 'Marzec', value: 3 },
      { text: 'Kwiecień', value: 4 }, { text: 'Maj', value: 5 }, { text: 'Czerwiec', value: 6 },
      { text: 'Lipiec', value: 7 }, { text: 'Sierpień', value: 8 }, { text: 'Wrzesień', value: 9 },
      { text: 'Październik', value: 10 }, { text: 'Listopad', value: 11 }, { text: 'Grudzień', value: 12 },
    ],
  }),
  computed: {
    previewSrc() {
      return this.pendingDataUrl || this.logoUrl || null;
    },
    isSuperAdmin() {
      return isSuperAdminFn();
    },
    acceptHeaders() {
      return [
        { text: this.$t('systemSettings.driverTermsColDriver'), value: 'driver', sortable: false },
        { text: 'E-mail', value: 'email', sortable: false },
        { text: this.$t('systemSettings.driverTermsColVersion'), value: 'version', sortable: false },
        { text: this.$t('systemSettings.driverTermsColDate'), value: 'createdAt', sortable: false },
        { text: this.$t('systemSettings.driverTermsColIp'), value: 'ipAddress', sortable: false },
      ];
    },
    yearOptions() {
      const y = new Date().getFullYear();
      return [y - 2, y - 1, y, y + 1];
    },
  },
  async mounted() {
    await this.fetchCompany();
    await this.fetchOcrConfig();
    await this.fetchCashReportConfig();
    this.fetchRachunkiConfig();
    this.fetchFakturyMailConfig();
    this.fetchAsystentConfig();
    await this.fetchNotifTestConfig();
    await this.loadOpeningBalance();
    await this.fetchDriverTerms();
    this.pobierzUrzadzeniaPodpisu();
  },
  watch: {
    'balance.year'() { this.loadOpeningBalance(); },
    'balance.month'() { this.loadOpeningBalance(); },
    'acceptDialog.options': {
      handler() { if (this.acceptDialog.show) this.fetchAcceptances(); },
      deep: true,
    },
    'acceptDialog.search'() {
      this.acceptDialog.options.page = 1;
      this.fetchAcceptances();
    },
  },
  methods: {
    async fetchOcrConfig() {
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('accounting/ocr/settings'), headers: this.authHeaders() });
        this.ocr.enabled = !!res.data.data.enabled;
        this.ocr.configured = !!res.data.data.configured;
      } catch (e) { /* brak dostępu do modułu — sekcja i tak nieszkodliwa */ }
    },
    async saveOcr() {
      this.ocr.saving = true;
      try {
        await axios({
          method: 'post',
          url: this.apiUrl('accounting/ocr/settings'),
          headers: this.authHeaders(),
          data: { enabled: this.ocr.enabled },
        });
        this.snackbar = { show: true, color: 'success', text: this.ocr.enabled
          ? 'OCR faktur włączony — odczyt uruchomi się przy otwieraniu faktur do akceptacji.'
          : 'OCR faktur wyłączony.' };
      } catch (e) {
        this.ocr.enabled = !this.ocr.enabled; // cofnij przełącznik
        this.snackbar = { show: true, color: 'error', text: e.response?.data?.message?.toString() || e.message };
      } finally {
        this.ocr.saving = false;
      }
    },
    authHeaders() {
      return { Authorization: 'Bearer ' + localStorage.getItem('token') };
    },
    apiUrl(p) { return process.env.VUE_APP_ROOT_API + p; },
    async fetchDriverTerms() {
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('driver-terms'), headers: this.authHeaders() });
        const d = (res.data && res.data.data) || {};
        this.terms.exists = !!d.exists;
        this.terms.originalName = d.originalName || '';
        this.terms.version = d.version || null;
        this.terms.updatedAt = d.updatedAt || null;
      } catch (e) { /* ignore */ }
    },
    formatTermsDate(d) { return d ? new Date(d).toLocaleDateString('pl-PL') : ''; },
    /**
     * Tablet uznajemy za milczacy po 24 h bez kontaktu. Tablet, ktory pracuje,
     * odpytuje serwer caly czas — wiec dluga cisza znaczy, ze to zostawiony
     * wpis po przeparowaniu i podpis na niego nie dojdzie.
     */
    milczy(u) {
      if (!u.lastSeenAt) return true;
      return Date.now() - new Date(u.lastSeenAt).getTime() > 24 * 60 * 60 * 1000;
    },
    async pobierzUrzadzeniaPodpisu() {
      this.podpisy.ladowanie = true;
      try {
        const r = await axios.get(process.env.VUE_APP_ROOT_API + 'signature/devices', {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        // Od ostatnio widzianego — ten sam porzadek, w ktorym wybierany jest
        // tablet do wyslania prosby o podpis.
        this.podpisy.urzadzenia = (r.data.data || [])
          .slice()
          .sort((a, b) => new Date(b.lastSeenAt || 0) - new Date(a.lastSeenAt || 0));
      } catch (e) {
        this.podpisy.urzadzenia = [];
      } finally {
        this.podpisy.ladowanie = false;
      }
    },
    async usunUrzadzeniePodpisu(u) {
      if (!confirm(this.$t('systemSettings.tabletRemoveConfirm', { name: u.name }))) return;
      this.podpisy.usuwanie = u.id;
      try {
        await axios.delete(process.env.VUE_APP_ROOT_API + 'signature/devices/' + u.id, {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.snackbar = { show: true, color: 'success', text: this.$t('systemSettings.tabletRemoved') };
        await this.pobierzUrzadzeniaPodpisu();
      } catch (e) {
        this.snackbar = {
          show: true, color: 'error',
          text: (e.response && e.response.data && e.response.data.message)
            || this.$t('systemSettings.tabletRemoveError'),
        };
      } finally {
        this.podpisy.usuwanie = '';
      }
    },
    formatDateTime(d) { return d ? new Date(d).toLocaleString('pl-PL') : '—'; },
    openAcceptancesDialog() {
      this.acceptDialog.show = true;
      this.acceptDialog.tab = 0;
      this.fetchAcceptances();
      this.fetchMissing();
    },
    async fetchAcceptances() {
      this.acceptDialog.loading = true;
      try {
        const res = await axios({
          method: 'get',
          url: this.apiUrl('driver-terms/acceptances'),
          headers: this.authHeaders(),
          params: {
            take: this.acceptDialog.options.itemsPerPage,
            skip: this.acceptDialog.options.page,
            search: this.acceptDialog.search || '',
          },
        });
        const d = (res.data && res.data.data) || {};
        this.acceptDialog.items = d.result || [];
        this.acceptDialog.total = d.count || 0;
        this.acceptDialog.currentVersion = d.currentVersion;
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: this.$t('systemSettings.driverTermsAcceptancesError') };
      } finally {
        this.acceptDialog.loading = false;
      }
    },
    async fetchMissing() {
      try {
        const res = await axios({
          method: 'get',
          url: this.apiUrl('driver-terms/missing'),
          headers: this.authHeaders(),
        });
        this.acceptDialog.missing = ((res.data && res.data.data) || {}).result || [];
      } catch (e) { /* zakładka pokaże pustą listę */ }
    },
    async previewTerms() {
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('driver-terms/file'), headers: this.authHeaders(), responseType: 'blob' });
        const blob = new Blob([res.data], { type: 'application/pdf' });
        window.open(URL.createObjectURL(blob), '_blank');
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: this.$t('systemSettings.driverTermsPreviewError') };
      }
    },
    async uploadTerms() {
      if (!this.terms.file) return;
      if (this.terms.file.type !== 'application/pdf') {
        this.snackbar = { show: true, color: 'error', text: this.$t('systemSettings.driverTermsPdfOnly') };
        return;
      }
      this.terms.saving = true;
      try {
        const fd = new FormData();
        fd.append('file', this.terms.file, this.terms.file.name);
        await axios({ method: 'post', url: this.apiUrl('driver-terms'), data: fd, headers: { ...this.authHeaders(), 'Content-Type': 'multipart/form-data' } });
        this.terms.file = null;
        await this.fetchDriverTerms();
        this.snackbar = { show: true, color: 'success', text: this.$t('systemSettings.driverTermsUploaded') };
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: (e.response && e.response.data && e.response.data.message) || this.$t('systemSettings.driverTermsUploadError') };
      } finally {
        this.terms.saving = false;
      }
    },
    async fetchRachunkiConfig() {
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('receipts/config'), headers: this.authHeaders() });
        const d = (res.data && res.data.data) || {};
        this.rachunki.enabled = !!d.rachunkiMonthlyEnabled;
        this.rachunki.email = d.rachunkiMonthlyEmail || '';
      } catch (e) { /* ignore */ }
    },
    async saveRachunkiConfig() {
      this.rachunki.saving = true;
      try {
        await axios({
          method: 'put',
          url: this.apiUrl('receipts/config'),
          headers: this.authHeaders(),
          data: { enabled: this.rachunki.enabled, email: this.rachunki.email || null },
        });
        this.snackbar = { show: true, color: 'success', text: 'Zapisano konfigurację rachunków' };
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: 'Nie udało się zapisać' };
      } finally {
        this.rachunki.saving = false;
      }
    },
    async testRachunkiNow() {
      this.rachunki.testing = true;
      try {
        const res = await axios({ method: 'post', url: this.apiUrl('receipts/test-monthly'), headers: this.authHeaders() });
        const d = (res.data && res.data.data) || {};
        if (d.sent > 0) this.snackbar = { show: true, color: 'success', text: `Wysłano: ${d.sent}` };
        else if (d.mocked > 0) this.snackbar = { show: true, color: 'warning', text: 'Tryb MOCK — SMTP nieskonfigurowany.' };
        else if (d.total === 0) this.snackbar = { show: true, color: 'warning', text: 'Brak firm z włączoną wysyłką. Zapisz konfigurację najpierw.' };
        else this.snackbar = { show: true, color: 'error', text: 'Wysyłka nie powiodła się' };
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: 'Nie udało się wysłać' };
      } finally {
        this.rachunki.testing = false;
      }
    },
    async fetchAsystentConfig() {
      try {
        const r = await axios({ method: 'get', url: this.apiUrl('assistant/status'), headers: this.authHeaders() });
        const d = (r.data && r.data.data) || {};
        this.asystent.enabled = !!d.wlaczony;
        this.asystent.skonfigurowany = !!d.skonfigurowany;
      } catch (e) { /* ignore */ }
    },
    async zapiszAsystenta() {
      this.asystent.saving = true;
      try {
        await axios({
          method: 'put',
          url: this.apiUrl('assistant/config'),
          headers: this.authHeaders(),
          data: { enabled: this.asystent.enabled },
        });
        this.snackbar = {
          show: true, color: 'success',
          text: this.asystent.enabled ? 'Asystent włączony' : 'Asystent wyłączony',
        };
      } catch (e) {
        this.asystent.enabled = !this.asystent.enabled;
        this.snackbar = { show: true, color: 'error', text: 'Nie udało się zapisać' };
      } finally {
        this.asystent.saving = false;
      }
    },
    async fetchFakturyMailConfig() {
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('invoices/email-config'), headers: this.authHeaders() });
        const d = (res.data && res.data.data) || {};
        this.fakturyMail.enabled = !!d.invoiceEmailEnabled;
        this.fakturyMail.email = d.invoiceEmailAddress || '';
        this.fakturyMail.withDetails = !!d.invoiceEmailWithDetails;
      } catch (e) { /* ignore */ }
    },
    async saveFakturyMailConfig() {
      this.fakturyMail.saving = true;
      try {
        await axios({
          method: 'put',
          url: this.apiUrl('invoices/email-config'),
          headers: this.authHeaders(),
          data: {
            enabled: this.fakturyMail.enabled,
            email: this.fakturyMail.email || null,
            withDetails: this.fakturyMail.withDetails,
          },
        });
        this.snackbar = { show: true, color: 'success', text: 'Zapisano wysyłkę faktur do księgowości' };
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: 'Nie udało się zapisać' };
      } finally {
        this.fakturyMail.saving = false;
      }
    },
    async wyslijZalegleFaktury() {
      this.fakturyMail.testing = true;
      try {
        const res = await axios({ method: 'post', url: this.apiUrl('invoices/email-retry'), headers: this.authHeaders() });
        const d = (res.data && res.data.data) || {};
        if (!d.znalezione) this.snackbar = { show: true, color: 'success', text: 'Brak zaległych faktur — wszystko wysłane' };
        else if (d.wyslane) this.snackbar = { show: true, color: 'success', text: `Wysłano ${d.wyslane} z ${d.znalezione}` };
        else this.snackbar = { show: true, color: 'error', text: 'Nie udało się wysłać — sprawdź konfigurację poczty' };
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: 'Nie udało się wysłać' };
      } finally {
        this.fakturyMail.testing = false;
      }
    },
    async fetchCashReportConfig() {
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('cash-report/config'), headers: this.authHeaders() });
        const d = (res.data && res.data.data) || {};
        this.cashReport.enabled = !!d.cashReportMonthlyEnabled;
        this.cashReport.email = d.cashReportMonthlyEmail || '';
      } catch (e) { /* ignore */ }
    },
    async saveCashReportConfig() {
      this.cashReport.saving = true;
      try {
        await axios({
          method: 'put',
          url: this.apiUrl('cash-report/config'),
          headers: this.authHeaders(),
          data: { enabled: this.cashReport.enabled, email: this.cashReport.email || null },
        });
        this.snackbar = { show: true, color: 'success', text: 'Zapisano konfigurację raportu' };
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: 'Nie udało się zapisać' };
      } finally {
        this.cashReport.saving = false;
      }
    },
    async fetchNotifTestConfig() {
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('notification-settings'), headers: this.authHeaders() });
        const d = (res.data && res.data.data) || {};
        this.notifTest.enabled = !!d.notifTestModeEnabled;
        this.notifTest.phone = d.notifTestPhone || '';
        this.notifTest.email = d.notifTestEmail || '';
      } catch (e) { /* ignore */ }
    },
    async saveNotifTestConfig() {
      this.notifTest.saving = true;
      try {
        const res = await axios({
          method: 'put',
          url: this.apiUrl('notification-settings'),
          headers: this.authHeaders(),
          data: {
            enabled: this.notifTest.enabled,
            phone: this.notifTest.phone ? this.notifTest.phone.trim() : null,
            email: this.notifTest.email ? this.notifTest.email.trim() : null,
          },
        });
        const d = (res.data && res.data.data) || {};
        // Pokaż znormalizowany numer (z prefiksem +48) po zapisie.
        this.notifTest.phone = d.notifTestPhone || '';
        this.notifTest.email = d.notifTestEmail || '';
        this.snackbar = { show: true, color: 'success', text: 'Zapisano ustawienia trybu testowego' };
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: 'Nie udało się zapisać' };
      } finally {
        this.notifTest.saving = false;
      }
    },
    async loadOpeningBalance() {
      try {
        const r = await axios({
          method: 'get',
          url: this.apiUrl('cash-report'),
          params: { year: this.balance.year, month: this.balance.month },
          headers: this.authHeaders(),
        });
        const d = (r.data && r.data.data) || {};
        this.balance.amount = (d.openingBalance || 0) / 100;
      } catch (e) { /* ignore */ }
    },
    async saveOpeningBalance() {
      this.balance.saving = true;
      try {
        await axios({
          method: 'put',
          url: this.apiUrl('cash-report/opening-balance'),
          params: { year: this.balance.year, month: this.balance.month },
          headers: this.authHeaders(),
          data: { openingBalance: Math.round((this.balance.amount || 0) * 100) },
        });
        this.snackbar = { show: true, color: 'success', text: 'Zapisano saldo początkowe' };
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: 'Nie udało się zapisać salda' };
      } finally {
        this.balance.saving = false;
      }
    },
    async testCashReportNow() {
      this.cashReport.testing = true;
      try {
        const res = await axios({
          method: 'post',
          url: this.apiUrl('cash-report/test-monthly'),
          headers: this.authHeaders(),
        });
        const d = (res.data && res.data.data) || {};
        if (d.sent > 0) {
          this.snackbar = { show: true, color: 'success', text: `Wysłano: ${d.sent}` };
        } else if (d.mocked > 0) {
          this.snackbar = { show: true, color: 'warning', text: 'Tryb MOCK — SMTP nie jest skonfigurowany (MAIL_HOST/USER/PASSWORD w .env puste). Wiadomość zalogowana, ale nie wysłana.' };
        } else if (d.total === 0) {
          this.snackbar = { show: true, color: 'warning', text: 'Brak firm z włączoną wysyłką. Zapisz konfigurację najpierw.' };
        } else if (d.failed > 0) {
          const err = (d.details && d.details[0] && d.details[0].error) || 'nieznany błąd';
          this.snackbar = { show: true, color: 'error', text: `Błąd: ${err}` };
        } else {
          this.snackbar = { show: true, color: 'info', text: 'Zakończono' };
        }
      } catch (e) {
        this.snackbar = { show: true, color: 'error', text: 'Nie udało się wysłać' };
      } finally {
        this.cashReport.testing = false;
      }
    },

    // fetch/save/test/remove dla Bolta i Ubera przeniesione do IntegrationsPage.vue.
    formatDate(d) {
      if (!d) return '';
      const dt = new Date(d);
      return dt.toLocaleString(this.$i18n.locale === 'pl' ? 'pl-PL' : 'en-US');
    },

    async fetchCompany() {
      try {
        const activeId = localStorage.getItem('activeCompanyId');
        if (!activeId) {
          const res = await axios({ method: 'get', url: this.apiUrl('company'), headers: this.authHeaders() });
          const list = (res.data && res.data.data) ? res.data.data : [];
          if (list.length > 0) this.logoUrl = list[0].logoUrl || null;
          return;
        }
        const res = await axios({ method: 'get', url: this.apiUrl('company/' + activeId), headers: this.authHeaders() });
        const company = res.data && res.data.data;
        this.logoUrl = (company && company.logoUrl) ? company.logoUrl : null;
      } catch (e) {
        console.error('Failed to fetch company:', e);
      }
    },

    onPick(e) {
      const f = e.target.files && e.target.files[0];
      if (f) this.handleFile(f);
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    onDrop(e) {
      this.dragOver = false;
      const f = e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) this.handleFile(f);
    },
    handleFile(f) {
      this.errorMsg = '';
      if (!f.type.startsWith('image/')) { this.errorMsg = 'Plik musi być obrazem.'; return; }
      if (f.size > 2 * 1024 * 1024) { this.errorMsg = 'Plik za duży (max 2 MB).'; return; }
      this.pendingFile = f;
      this.pendingName = f.name;
      this.pendingSize = (f.size / 1024).toFixed(0) + ' KB';
      const r = new FileReader();
      r.onload = e => { this.pendingDataUrl = e.target.result; };
      r.readAsDataURL(f);
    },
    resetPending() {
      this.pendingFile = null;
      this.pendingDataUrl = null;
      this.pendingName = '';
      this.pendingSize = '';
      this.errorMsg = '';
    },
    async save() {
      if (!this.pendingFile) return;
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append('file', this.pendingFile);
        const res = await axios({
          method: 'post',
          url: this.apiUrl('company/logo'),
          data: fd,
          headers: { ...this.authHeaders(), 'Content-Type': 'multipart/form-data' },
        });
        const url = res.data && res.data.data && res.data.data.logoUrl;
        if (url) {
          this.logoUrl = url;
          this.$root.$emit('logo-updated', url);
        }
        this.resetPending();
        this.showSnack('Logo zapisane', 'success');
      } catch (e) {
        console.error('Upload failed:', e);
        this.showSnack('Nie udało się zapisać logo', 'error');
      }
      this.saving = false;
    },
    async removeLogo() {
      this.removing = true;
      try {
        await axios({ method: 'delete', url: this.apiUrl('company/logo'), headers: this.authHeaders() });
        this.logoUrl = null;
        this.$root.$emit('logo-updated', null);
        this.showSnack('Logo usunięte', 'success');
      } catch (e) {
        console.error('Delete failed:', e);
        this.showSnack('Nie udało się usunąć logo', 'error');
      }
      this.removing = false;
    },
    showSnack(text, color) { this.snackbar = { show: true, text, color }; },
  },
};
</script>

<style scoped>
.system-settings-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.ssp-inner {
  max-width: 900px;
  margin: 0 auto;
}
.system-settings-page >>> .v-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-color: #2A3142 !important;
}
.system-settings-page >>> .v-card__title {
  color: #FFFFFF !important;
}
.system-settings-page >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.system-settings-page >>> .v-text-field input,
.system-settings-page >>> .v-text-field textarea,
.system-settings-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.system-settings-page >>> .v-label {
  color: #94A0B8 !important;
}
.system-settings-page >>> .grey--text {
  color: #94A0B8 !important;
}
.logo-preview-wrap {
  background: #1F2433;
  border: 1px solid #2A3142;
  border-radius: 12px;
  padding: 24px;
  min-height: 240px;
  display: flex; align-items: center; justify-content: center;
}
.logo-preview { width: 100%; display: flex; align-items: center; justify-content: center; }
.logo-preview img {
  max-width: 100%;
  max-height: 200px;
  width: auto;
  height: auto;
  display: block;
  object-fit: contain;
}
.logo-placeholder { text-align: center; color: #90A4AE; }

.upload-drop {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  background: rgba(255,255,255,0.02);
  border: 2px dashed rgba(255,255,255,0.15);
  border-radius: 10px; padding: 28px 20px; cursor: pointer;
  transition: all .15s;
}
.upload-drop:hover, .upload-drop.dragover {
  border-color: #3F51B5; background: rgba(63,81,181,0.08);
}
.upload-title { font-size: 14px; font-weight: 600; color: white; margin-top: 4px; }
.upload-sub { font-size: 12px; color: #90A4AE; }
</style>
